import { Steps, Tag, Tooltip } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  EllipsisOutlined,
} from "@ant-design/icons";
import type {
  IntakeWorkflow,
  WorkflowStage,
  WorkflowStageId,
} from "../intakeDetails/types";
import { STAGE_LABELS } from "../intakeDetails/types";

interface Props {
  workflow: IntakeWorkflow | null;
  activeStage: WorkflowStageId;
  onStageClick: (stageId: WorkflowStageId) => void;
}

const STAGE_ORDER: WorkflowStageId[] = [
  "schedule_meeting",
  "scoring_chart",
  "vendor_matching",
  "ai_advisory_board_presentation",
  "leadership_presentation",
  "approval_decision",
];

function statusTag(stage: WorkflowStage) {
  if (stage.status === "completed") {
    return <Tag color="success">Complete</Tag>;
  }
  if (stage.status === "in-progress") {
    return <Tag color="processing">In progress</Tag>;
  }
  return <Tag>Pending</Tag>;
}

function statusIcon(status: WorkflowStage["status"]) {
  if (status === "completed") return <CheckCircleOutlined />;
  if (status === "in-progress") return <ClockCircleOutlined />;
  return <EllipsisOutlined />;
}

export default function StageStepper({ workflow, activeStage, onStageClick }: Props) {
  const stages: WorkflowStage[] = workflow?.stages ?? STAGE_ORDER.map((id) => ({
    id,
    name: STAGE_LABELS[id],
    status: "pending" as const,
  }));

  const activeIndex = Math.max(
    0,
    stages.findIndex((stage) => stage.id === activeStage)
  );

  // Gate clicks: only past + current stages are clickable. If workflow is
  // null (defensive — auto-create should ensure this never happens) only the
  // first stage is reachable.
  const currentStageIndex = workflow
    ? Math.max(
        0,
        stages.findIndex((stage) => stage.id === workflow.currentStage)
      )
    : 0;

  const handleChange = (index: number) => {
    if (index <= currentStageIndex) {
      onStageClick(stages[index].id);
    }
    // future stages: no-op (Use the Advance button to move forward)
  };

  return (
    <nav aria-label="Workflow stages">
      <Steps
        current={activeIndex}
        size="small"
        onChange={handleChange}
        items={stages.map((stage, index) => {
          const isFuture = index > currentStageIndex;
          const titleNode = (
            <span aria-current={stage.id === activeStage ? "step" : undefined}>
              {STAGE_LABELS[stage.id]}
            </span>
          );
          const itemStatus: "finish" | "process" | "wait" =
            stage.status === "completed"
              ? "finish"
              : stage.status === "in-progress"
                ? "process"
                : "wait";
          return {
            title: isFuture ? (
              <Tooltip title="Use 'Advance to next stage' to move forward">
                {titleNode}
              </Tooltip>
            ) : (
              titleNode
            ),
            description: statusTag(stage),
            icon: statusIcon(stage.status),
            disabled: isFuture,
            status: itemStatus,
          };
        })}
      />
    </nav>
  );
}
